import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { normalizePhoneInput } from '@/lib/phone';
import { formatDate } from '@/lib/dates';
import {
  Loader2,
  Save,
  X,
  Building2,
  CreditCard,
  UserCircle,
  Settings,
  Search,
  Link2,
  MapPin,
  ShieldCheck,
  CalendarDays,
  FileCheck,
  Route,
  AlertCircle,
  PhoneCall,
  PhoneForwarded,
} from 'lucide-react';
import CRMAccountNumberPicker, {
  type SubAccountConfig,
  detectSeriesFromAccountId,
} from './CRMAccountNumberPicker';
import CRMBookableAddresses from './CRMBookableAddresses';

export interface CRMAccount {
  id: string;
  account_id: string;
  account_name: string;
  account_type: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  billing_address: string | null;
  notes: string | null;
  is_active: boolean;
  default_project: string | null;
  default_cost_code: string | null;
  default_reference: string | null;
  created_at: string;
  updated_at: string;
  parent_account_id: string | null;
  billing_account_id: string | null;
  account_category: string | null;
  hierarchy_type: string | null;
  use_parent_billing_defaults: boolean;
  use_parent_operational_defaults: boolean;
  billed_status: string | null;
  account_series_type: string | null;
  account_number_numeric: number | null;
  billing_name: string | null;
  billing_contact_name: string | null;
  billing_email: string | null;
  billing_phone: string | null;
  billing_address_line_1: string | null;
  billing_address_line_2: string | null;
  billing_suburb: string | null;
  billing_city: string | null;
  billing_postcode: string | null;
  billing_attention: string | null;
  invoice_email: string | null;
  accounts_payable_email: string | null;
  payment_terms: string | null;
  billing_notes: string | null;
  traveller_first_name: string | null;
  traveller_last_name: string | null;
  traveller_full_name: string | null;
  traveller_phone: string | null;
  traveller_mobile: string | null;
  traveller_email: string | null;
  traveller_notes: string | null;
  mobility_notes: string | null;
  preferred_vehicle: string;
  pickup_notes: string | null;
  dropoff_notes: string | null;
  external_client_ref: string | null;
  requires_purchase_order: boolean;
  requires_project: boolean;
  requires_reference: boolean;
  requires_cost_code: boolean;
  default_fleet: string | null;
  default_stage: string | null;
  allow_direct_settlement: boolean;
  active_from: string | null;
  active_to: string | null;
  open_approval: boolean;
  is_fixed_location_number: boolean;
  always_divert_to_operator: boolean;
}

/** Where the form should open: which tab, and optionally which section to
 *  scroll to (ids are `crm-form-<section>`). Used by the client overview tiles. */
export interface CRMFormFocus {
  tab: 'basic' | 'hierarchy' | 'billing' | 'traveller' | 'operational';
  section?: 'ivr' | 'addresses' | 'transport';
}

interface CRMAccountFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: CRMAccount | null;
  onSaved: () => void;
  subAccountConfig?: SubAccountConfig | null;
  focus?: CRMFormFocus | null;
}

const ACCOUNT_CATEGORIES = [
  'Parent',
  'Sub Account',
  'Traveller',
  'Corporate',
  'Personal',
  'Facility',
  'Funder',
  'Internal',
  'Other',
];

const HIERARCHY_TYPES = ['Parent', 'Child', 'Traveller'];

const PAYMENT_TERMS_OPTIONS = [
  'Due on Receipt',
  'Net 7',
  'Net 14',
  'Net 20',
  'Net 30',
  'Net 60',
  'Net 90',
  '20th of Following Month',
];

const EMPTY_FORM = {
  account_id: '',
  account_name: '',
  account_type: '',
  contact_name: '',
  email: '',
  phone: '',
  billing_address: '',
  notes: '',
  is_active: true,
  default_project: '',
  default_cost_code: '',
  default_reference: '',
  parent_account_id: '',
  billing_account_id: '',
  account_category: '',
  hierarchy_type: '',
  use_parent_billing_defaults: false,
  use_parent_operational_defaults: false,
  billed_status: '',
  account_series_type: '',
  account_number_numeric: null as number | null,
  billing_name: '',
  billing_contact_name: '',
  billing_email: '',
  billing_phone: '',
  billing_address_line_1: '',
  billing_address_line_2: '',
  billing_suburb: '',
  billing_city: '',
  billing_postcode: '',
  billing_attention: '',
  invoice_email: '',
  accounts_payable_email: '',
  payment_terms: '',
  billing_notes: '',
  traveller_first_name: '',
  traveller_last_name: '',
  traveller_full_name: '',
  traveller_phone: '',
  traveller_mobile: '',
  traveller_email: '',
  traveller_notes: '',
  mobility_notes: '',
  preferred_vehicle: 'not_specified',
  pickup_notes: '',
  dropoff_notes: '',
  external_client_ref: '',
  requires_purchase_order: false,
  requires_project: false,
  requires_reference: false,
  requires_cost_code: false,
  default_fleet: '',
  default_stage: '',
  allow_direct_settlement: false,
  active_from: '',
  active_to: '',
  open_approval: false,
  is_fixed_location_number: false,
  always_divert_to_operator: false,
};

const isoToDisplay = (isoDate: string): string => {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const displayToIso = (display: string): string => {
  if (!display || !display.trim()) return '';
  const cleaned = display.trim();

  const match = cleaned.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (match) {
    const day = match[1].padStart(2, '0');
    const month = match[2].padStart(2, '0');
    const year = match[3];
    return `${year}-${month}-${day}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) return cleaned;
  return '';
};

const isValidDisplayDate = (display: string): boolean => {
  if (!display || !display.trim()) return true;
  const iso = displayToIso(display);
  if (!iso) return false;
  const d = new Date(`${iso}T00:00:00`);
  return !isNaN(d.getTime());
};

const getApprovalStatus = (activeFrom: string, activeTo: string) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const fromIso = displayToIso(activeFrom);
  const toIso = displayToIso(activeTo);

  const fromDate = fromIso ? new Date(`${fromIso}T00:00:00`) : null;
  const toDate = toIso ? new Date(`${toIso}T00:00:00`) : null;

  if (!fromDate && !toDate) {
    return {
      label: 'No approval dates set',
      tone: 'muted' as const,
      daysRemaining: null as number | null,
      description: 'No active approval range is stored on this account.',
    };
  }

  if (fromDate && today < fromDate) {
    const diff = Math.ceil((fromDate.getTime() - today.getTime()) / 86400000);
    return {
      label: 'Upcoming',
      tone: 'info' as const,
      daysRemaining: diff,
      description: `Approval starts in ${diff} day${diff === 1 ? '' : 's'}.`,
    };
  }

  if (toDate && today > toDate) {
    const diff = Math.ceil((today.getTime() - toDate.getTime()) / 86400000);
    return {
      label: 'Expired',
      tone: 'danger' as const,
      daysRemaining: -diff,
      description: `Approval expired ${diff} day${diff === 1 ? '' : 's'} ago.`,
    };
  }

  if (toDate) {
    const diff = Math.ceil((toDate.getTime() - today.getTime()) / 86400000);
    return {
      label: 'Active',
      tone: 'success' as const,
      daysRemaining: diff,
      description: `${diff} day${diff === 1 ? '' : 's'} remaining.`,
    };
  }

  return {
    label: 'Active',
    tone: 'success' as const,
    daysRemaining: null as number | null,
    description: 'Approval is open-ended from the current start date.',
  };
};

const getStatusBadgeClass = (tone: 'muted' | 'info' | 'success' | 'danger') => {
  switch (tone) {
    case 'success':
      return 'bg-green-900/40 text-green-400 border-green-700/50';
    case 'danger':
      return 'bg-red-900/40 text-red-400 border-red-700/50';
    case 'info':
      return 'bg-blue-900/40 text-blue-400 border-blue-700/50';
    default:
      return 'bg-secondary text-muted-foreground border-border';
  }
};

// NOTE: These small field helpers MUST be defined at module scope (not inside
// CRMAccountForm). If they are re-declared inside the parent render, React sees
// a brand-new component type on every keystroke, unmounts/remounts the <Input>,
// and the input loses focus after each character — which manifested as
// "only one letter at a time" typing in the CRM form.
interface FieldProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  type?: string;
  span2?: boolean;
  required?: boolean;
  error?: string;
}

const Field: React.FC<FieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  span2 = false,
  required = false,
  error,
}) => (
  <div className={`space-y-1.5 ${span2 ? 'md:col-span-2' : ''}`}>
    <Label className="text-foreground text-sm">
      {label} {required && <span className="text-red-400">*</span>}
    </Label>
    <Input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(type === 'tel' ? normalizePhoneInput(e.target.value) : e.target.value)}
      className="dark-input"
      placeholder={placeholder}
    />
    {error && <p className="text-red-400 text-xs">{error}</p>}
  </div>
);

interface TextareaFieldProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  span2?: boolean;
}

const TextareaField: React.FC<TextareaFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  span2 = false,
}) => (
  <div className={`space-y-1.5 ${span2 ? 'md:col-span-2' : ''}`}>
    <Label className="text-foreground text-sm">{label}</Label>
    <Textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="dark-input min-h-[60px]"
      placeholder={placeholder}
    />
  </div>
);

interface SwitchFieldProps {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  description?: string;
}

const SwitchField: React.FC<SwitchFieldProps> = ({
  label,
  checked,
  onCheckedChange,
  description,
}) => (
  <div className="flex items-center justify-between py-1">
    <div>
      <Label className="text-foreground text-sm">{label}</Label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
    <Switch checked={checked ?? false} onCheckedChange={onCheckedChange} />
  </div>
);

const CRMAccountForm: React.FC<CRMAccountFormProps> = ({
  open,
  onOpenChange,
  account,
  onSaved,
  subAccountConfig,
  focus,
}) => {

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('basic');
  // Read inside the reset effect below without making it re-run (and wipe
  // in-progress edits) whenever the parent changes `focus`.
  const focusRef = React.useRef<CRMFormFocus | null | undefined>(focus);
  focusRef.current = focus;

  const [parentSearch, setParentSearch] = useState('');
  const [parentResults, setParentResults] = useState<{ account_id: string; account_name: string }[]>([]);
  const [searchingParent, setSearchingParent] = useState(false);
  const [showParentSearch, setShowParentSearch] = useState(false);

  const [billingSearch, setBillingSearch] = useState('');
  const [billingResults, setBillingResults] = useState<{ account_id: string; account_name: string }[]>([]);
  const [searchingBilling, setSearchingBilling] = useState(false);
  const [showBillingSearch, setShowBillingSearch] = useState(false);

  const isEditing = !!account;

  useEffect(() => {
    if (account) {
      const dbSeriesType = account.account_series_type || '';
      const dbBilledStatus = account.billed_status || '';
      let resolvedSeriesType = dbSeriesType;
      let resolvedBilledStatus = dbBilledStatus;

      if (!resolvedSeriesType || !resolvedBilledStatus) {
        const detected = detectSeriesFromAccountId(account.account_id || '');
        if (!resolvedSeriesType && detected.seriesType) {
          resolvedSeriesType = detected.seriesType;
        }
        if (!resolvedBilledStatus && detected.billedStatus) {
          resolvedBilledStatus = detected.billedStatus;
        }
      }

      setForm({
        account_id: account.account_id || '',
        account_name: account.account_name || '',
        account_type: account.account_type || '',
        contact_name: account.contact_name || '',
        email: account.email || '',
        phone: account.phone || '',
        billing_address: account.billing_address || '',
        notes: account.notes || '',
        is_active: account.is_active ?? true,
        default_project: account.default_project || '',
        default_cost_code: account.default_cost_code || '',
        default_reference: account.default_reference || '',
        parent_account_id: account.parent_account_id || '',
        billing_account_id: account.billing_account_id || '',
        account_category: account.account_category || '',
        hierarchy_type: account.hierarchy_type || '',
        use_parent_billing_defaults: account.use_parent_billing_defaults ?? false,
        use_parent_operational_defaults: account.use_parent_operational_defaults ?? false,
        billed_status: resolvedBilledStatus,
        account_series_type: resolvedSeriesType,
        account_number_numeric: account.account_number_numeric ?? null,
        billing_name: account.billing_name || '',
        billing_contact_name: account.billing_contact_name || '',
        billing_email: account.billing_email || '',
        billing_phone: account.billing_phone || '',
        billing_address_line_1: account.billing_address_line_1 || '',
        billing_address_line_2: account.billing_address_line_2 || '',
        billing_suburb: account.billing_suburb || '',
        billing_city: account.billing_city || '',
        billing_postcode: account.billing_postcode || '',
        billing_attention: account.billing_attention || '',
        invoice_email: account.invoice_email || '',
        accounts_payable_email: account.accounts_payable_email || '',
        payment_terms: account.payment_terms || '',
        billing_notes: account.billing_notes || '',
        traveller_first_name: account.traveller_first_name || '',
        traveller_last_name: account.traveller_last_name || '',
        traveller_full_name: account.traveller_full_name || '',
        traveller_phone: account.traveller_phone || '',
        traveller_mobile: account.traveller_mobile || '',
        traveller_email: account.traveller_email || '',
        traveller_notes: account.traveller_notes || '',
        mobility_notes: account.mobility_notes || '',
        preferred_vehicle: account.preferred_vehicle || 'not_specified',
        pickup_notes: account.pickup_notes || '',
        dropoff_notes: account.dropoff_notes || '',
        external_client_ref: account.external_client_ref || '',
        requires_purchase_order: account.requires_purchase_order ?? false,
        requires_project: account.requires_project ?? false,
        requires_reference: account.requires_reference ?? false,
        requires_cost_code: account.requires_cost_code ?? false,
        default_fleet: account.default_fleet || '',
        default_stage: account.default_stage || '',
        allow_direct_settlement: account.allow_direct_settlement ?? false,
        active_from: account.active_from ? isoToDisplay(account.active_from.split('T')[0]) : '',
        active_to: account.active_to ? isoToDisplay(account.active_to.split('T')[0]) : '',
        open_approval: account.open_approval ?? false,
        is_fixed_location_number: account.is_fixed_location_number ?? false,
        always_divert_to_operator: account.always_divert_to_operator ?? false,
      });
    } else {
      const base = { ...EMPTY_FORM };
      if (subAccountConfig) {
        base.parent_account_id = subAccountConfig.parentAccountId;
        base.billed_status = 'Billed';

        if (subAccountConfig.mode === 'traveller') {
          base.hierarchy_type = 'Traveller';
          base.account_category = 'Traveller';
        } else if (subAccountConfig.mode === 'sub-account') {
          base.hierarchy_type = 'Child';
          base.account_category = 'Sub Account';
        } else if (subAccountConfig.mode === 'ooa') {
          base.hierarchy_type = 'Child';
          base.account_category = 'Sub Account';
        }
      }
      setForm(base);
    }

    setErrors({});
    setActiveTab(focusRef.current?.tab ?? 'basic');
    setShowParentSearch(false);
    setShowBillingSearch(false);
  }, [account, open, subAccountConfig]);

  useEffect(() => {
    if (!open || !focus?.section) return;
    const timer = setTimeout(() => {
      document.getElementById(`crm-form-${focus.section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => clearTimeout(timer);
  }, [open, focus]);

  useEffect(() => {
    if (!parentSearch.trim() || parentSearch.length < 2) {
      setParentResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingParent(true);
      try {
        const s = `%${parentSearch.trim()}%`;
        const { data } = await supabase
          .from('crm_accounts')
          .select('account_id, account_name')
          .or(`account_id.ilike.${s},account_name.ilike.${s}`)
          .neq('account_id', form.account_id || '__none__')
          .eq('is_active', true)
          .limit(10);

        setParentResults(data || []);
      } catch {
        setParentResults([]);
      } finally {
        setSearchingParent(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [parentSearch, form.account_id]);

  useEffect(() => {
    if (!billingSearch.trim() || billingSearch.length < 2) {
      setBillingResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingBilling(true);
      try {
        const s = `%${billingSearch.trim()}%`;
        const { data } = await supabase
          .from('crm_accounts')
          .select('account_id, account_name')
          .or(`account_id.ilike.${s},account_name.ilike.${s}`)
          .neq('account_id', form.account_id || '__none__')
          .eq('is_active', true)
          .limit(10);

        setBillingResults(data || []);
      } catch {
        setBillingResults([]);
      } finally {
        setSearchingBilling(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [billingSearch, form.account_id]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.account_id.trim()) newErrors.account_id = 'Account ID is required';
    if (!form.account_name.trim()) newErrors.account_name = 'Account Name is required';

    if (form.active_from && !isValidDisplayDate(form.active_from)) {
      newErrors.active_from = 'Invalid date format. Use dd/mm/yyyy';
    }

    if (form.active_to && !isValidDisplayDate(form.active_to)) {
      newErrors.active_to = 'Invalid date format. Use dd/mm/yyyy';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      if (newErrors.account_id || newErrors.account_name) {
        setActiveTab('basic');
      } else if (newErrors.active_from || newErrors.active_to) {
        setActiveTab('operational');
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    try {
      const numericId = /^\d+$/.test(form.account_id.trim()) ? parseInt(form.account_id.trim(), 10) : null;

      const detected = detectSeriesFromAccountId(form.account_id.trim());
      const effectiveSeriesType = detected.seriesType || form.account_series_type || '';
      const effectiveBilledStatus = detected.billedStatus || form.billed_status || '';

      const payload: Record<string, any> = {
        account_id: form.account_id.trim(),
        account_name: form.account_name.trim(),
        account_type: form.account_type.trim() || null,
        contact_name: form.contact_name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        billing_address: form.billing_address.trim() || null,
        notes: form.notes.trim() || null,
        is_active: form.is_active,
        default_project: form.default_project.trim() || null,
        default_cost_code: form.default_cost_code.trim() || null,
        default_reference: form.default_reference.trim() || null,
        parent_account_id: form.parent_account_id.trim() || null,
        billing_account_id: form.billing_account_id.trim() || null,
        account_category: form.account_category && form.account_category !== '__none' ? form.account_category : null,
        hierarchy_type: form.hierarchy_type && form.hierarchy_type !== '__none' ? form.hierarchy_type : null,
        use_parent_billing_defaults: form.use_parent_billing_defaults,
        use_parent_operational_defaults: form.use_parent_operational_defaults,
        billed_status: effectiveBilledStatus || null,
        account_series_type:
          effectiveSeriesType && effectiveSeriesType !== 'Non-Billed' ? effectiveSeriesType : null,
        account_number_numeric: numericId,
        billing_name: form.billing_name.trim() || null,
        billing_contact_name: form.billing_contact_name.trim() || null,
        billing_email: form.billing_email.trim() || null,
        billing_phone: form.billing_phone.trim() || null,
        billing_address_line_1: form.billing_address_line_1.trim() || null,
        billing_address_line_2: form.billing_address_line_2.trim() || null,
        billing_suburb: form.billing_suburb.trim() || null,
        billing_city: form.billing_city.trim() || null,
        billing_postcode: form.billing_postcode.trim() || null,
        billing_attention: form.billing_attention.trim() || null,
        invoice_email: form.invoice_email.trim() || null,
        accounts_payable_email: form.accounts_payable_email.trim() || null,
        payment_terms: form.payment_terms && form.payment_terms !== '__none' ? form.payment_terms : null,
        billing_notes: form.billing_notes.trim() || null,
        traveller_first_name: form.traveller_first_name.trim() || null,
        traveller_last_name: form.traveller_last_name.trim() || null,
        traveller_full_name:
          form.traveller_full_name.trim() ||
          ([form.traveller_first_name.trim(), form.traveller_last_name.trim()].filter(Boolean).join(' ') || null),
        traveller_phone: form.traveller_phone.trim() || null,
        traveller_mobile: form.traveller_mobile.trim() || null,
        traveller_email: form.traveller_email.trim() || null,
        traveller_notes: form.traveller_notes.trim() || null,
        mobility_notes: form.mobility_notes.trim() || null,
        preferred_vehicle: form.preferred_vehicle,
        pickup_notes: form.pickup_notes.trim() || null,
        dropoff_notes: form.dropoff_notes.trim() || null,
        external_client_ref: form.external_client_ref.trim() || null,
        requires_purchase_order: form.requires_purchase_order,
        requires_project: form.requires_project,
        requires_reference: form.requires_reference,
        requires_cost_code: form.requires_cost_code,
        default_fleet: form.default_fleet.trim() || null,
        default_stage: form.default_stage.trim() || null,
        allow_direct_settlement: form.allow_direct_settlement,
        active_from: form.active_from
          ? (() => {
              const iso = displayToIso(form.active_from);
              return iso ? new Date(`${iso}T00:00:00`).toISOString() : null;
            })()
          : null,
        active_to: form.active_to
          ? (() => {
              const iso = displayToIso(form.active_to);
              return iso ? new Date(`${iso}T00:00:00`).toISOString() : null;
            })()
          : null,
        open_approval: form.open_approval,
        is_fixed_location_number: form.is_fixed_location_number,
        always_divert_to_operator: form.always_divert_to_operator,
        updated_at: new Date().toISOString(),
      };

      if (isEditing && account) {
        const { error } = await supabase.from('crm_accounts').update(payload).eq('id', account.id);
        if (error) throw error;

        toast({
          title: 'Account Updated',
          description: `${payload.account_name} has been updated.`,
        });
      } else {
        const { error } = await supabase
          .from('crm_accounts')
          .insert({ ...payload, created_at: new Date().toISOString() });

        if (error) {
          if (error.code === '23505') {
            setErrors({ account_id: 'This Account ID already exists' });
            setActiveTab('basic');
            setSaving(false);
            return;
          }
          throw error;
        }

        toast({
          title: 'Account Created',
          description: `${payload.account_name} has been created.`,
        });
      }

      onSaved();
      // Editing an existing account stays open (per feedback: saving
      // shouldn't close the modal). A brand-new account still closes --
      // `account` stays null after create (the parent doesn't hand back
      // the new row), so staying open would leave the form saying "Create
      // Account" and re-submitting would insert a second row.
      if (!isEditing) onOpenChange(false);
    } catch (err: any) {
      console.error('Save error:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to save account',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleAccountNumberSelected = useCallback((accountId: string, seriesType: string, billedStatus: string) => {
    let resolvedSeries = seriesType;
    let resolvedBilled = billedStatus;

    if (!resolvedSeries || !resolvedBilled) {
      const detected = detectSeriesFromAccountId(accountId);
      if (!resolvedSeries && detected.seriesType) resolvedSeries = detected.seriesType;
      if (!resolvedBilled && detected.billedStatus) resolvedBilled = detected.billedStatus;
    }

    setForm((prev) => ({
      ...prev,
      account_id: accountId,
      account_series_type: resolvedSeries,
      billed_status: resolvedBilled,
      account_number_numeric: /^\d+$/.test(accountId) ? parseInt(accountId, 10) : null,
    }));
  }, []);

  // Field / TextareaField / SwitchField are defined at module scope above.
  // We wrap them with small inline helpers here that bind form state + errors,
  // but the underlying component identity is stable across renders so the
  // <Input>/<Textarea> inside is not unmounted when setForm runs, which fixes
  // the "only one character at a time" focus-loss bug.
  const renderField = (props: {
    label: string;
    field: string;
    placeholder?: string;
    type?: string;
    span2?: boolean;
    required?: boolean;
  }) => (
    <Field
      key={props.field}
      label={props.label}
      value={(form as any)[props.field] || ''}
      onChange={(val) => updateField(props.field, val)}
      placeholder={props.placeholder}
      type={props.type}
      span2={props.span2}
      required={props.required}
      error={errors[props.field]}
    />
  );

  const renderTextarea = (props: {
    label: string;
    field: string;
    placeholder?: string;
    span2?: boolean;
  }) => (
    <TextareaField
      key={props.field}
      label={props.label}
      value={(form as any)[props.field] || ''}
      onChange={(val) => updateField(props.field, val)}
      placeholder={props.placeholder}
      span2={props.span2}
    />
  );

  const renderSwitch = (props: {
    label: string;
    field: string;
    description?: string;
  }) => (
    <SwitchField
      key={props.field}
      label={props.label}
      checked={(form as any)[props.field] ?? false}
      onCheckedChange={(checked) => updateField(props.field, checked)}
      description={props.description}
    />
  );


  const approvalStatus = getApprovalStatus(form.active_from, form.active_to);

  const defaultSummaryItems = [
    { label: 'Project', value: form.default_project || 'Not set' },
    { label: 'Cost Code', value: form.default_cost_code || 'Not set' },
    { label: 'Reference', value: form.default_reference || 'Not set' },
    { label: 'Fleet', value: form.default_fleet || 'Not set' },
  ];

  const defaultsConfiguredCount = defaultSummaryItems.filter((item) => item.value !== 'Not set').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col bg-card border-border">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="golden-text text-xl">
            {isEditing
              ? 'Edit CRM Account'
              : subAccountConfig
                ? subAccountConfig.mode === 'traveller'
                  ? 'Create Traveller Account'
                  : subAccountConfig.mode === 'ooa'
                    ? 'Create Out of Area Account'
                    : 'Create Sub-Account'
                : 'Create New CRM Account'}
          </DialogTitle>

          {isEditing && (
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="font-mono text-xs">
                {account?.account_id}
              </Badge>
              {account?.account_series_type && (
                <Badge className="bg-blue-900/40 text-blue-400 border-blue-700/50 text-xs">
                  {account.account_series_type}
                </Badge>
              )}
              {account?.billed_status && (
                <Badge
                  className={`text-xs ${
                    account.billed_status === 'Billed'
                      ? 'bg-green-900/40 text-green-400 border-green-700/50'
                      : 'bg-secondary text-muted-foreground border-border'
                  }`}
                >
                  {account.billed_status}
                </Badge>
              )}
            </div>
          )}
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="flex-shrink-0 w-full grid grid-cols-5 bg-secondary/50">
            <TabsTrigger value="basic" className="text-xs gap-1">
              <Building2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Basic</span>
            </TabsTrigger>
            <TabsTrigger value="hierarchy" className="text-xs gap-1">
              <Link2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Hierarchy</span>
            </TabsTrigger>
            <TabsTrigger value="billing" className="text-xs gap-1">
              <CreditCard className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Billing</span>
            </TabsTrigger>
            <TabsTrigger value="traveller" className="text-xs gap-1">
              <UserCircle className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Traveller</span>
            </TabsTrigger>
            <TabsTrigger value="operational" className="text-xs gap-1">
              <Settings className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Operational</span>
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto py-4 px-1">
            <TabsContent value="basic" className="mt-0 space-y-4">
              {!isEditing && (
                <div className="border border-border rounded-lg p-4 bg-secondary/20">
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Account Number Assignment
                  </p>
                  <CRMAccountNumberPicker
                    value={form.account_id}
                    onChange={handleAccountNumberSelected}
                    subAccountConfig={subAccountConfig || undefined}
                  />
                </div>
              )}

              {isEditing && (
                <CRMAccountNumberPicker
                  value={form.account_id}
                  onChange={handleAccountNumberSelected}
                  editMode
                />
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderField({ label: 'Account Name', field: 'account_name', placeholder: 'Company or account name', required: true })}

                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Account Category</Label>
                  <Select value={form.account_category} onValueChange={(v) => updateField('account_category', v)}>
                    <SelectTrigger className="dark-input">
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">No Category</SelectItem>
                      {ACCOUNT_CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {renderField({ label: 'Contact Name', field: 'contact_name', placeholder: 'Primary contact' })}
                {renderField({ label: 'Email', field: 'email', placeholder: 'contact@example.com', type: 'email' })}
                {renderField({ label: 'Phone', field: 'phone', placeholder: 'Phone number', type: 'tel' })}


                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Account Type</Label>
                  <Select
                    value={form.billed_status || '__none'}
                    onValueChange={(v) => updateField('billed_status', v === '__none' ? '' : v)}
                  >
                    <SelectTrigger className="dark-input">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Not Set</SelectItem>
                      <SelectItem value="Billed">Billed</SelectItem>
                      <SelectItem value="Non-Billed">Non Billed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {renderTextarea({ label: 'Notes', field: 'notes', placeholder: 'Internal notes about this account...', span2: true })}

              {renderSwitch({ label: 'Active', field: 'is_active', description: 'Whether this account is currently active' })}

              <div id="crm-form-ivr" className="border border-border rounded-lg p-4 bg-secondary/20 space-y-3">
                <div className="flex items-center gap-2">
                  <PhoneCall className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    IVR / Caller ID
                  </p>
                </div>

                <div className="flex items-center justify-between py-2 px-3 rounded-lg border border-border bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <MapPin
                      className={`w-4 h-4 ${form.is_fixed_location_number ? 'text-green-400' : 'text-muted-foreground'}`}
                    />
                    <div>
                      <Label className="text-foreground text-sm">Fixed Location Number</Label>
                      <p className="text-xs text-muted-foreground">
                        This account's Phone is a recognised caller ID (e.g. a supermarket) -- the IVR skips asking for
                        an address and offers this account's approved pickup address instead
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={form.is_fixed_location_number}
                    onCheckedChange={(checked) => updateField('is_fixed_location_number', checked)}
                  />
                </div>

                <div className="flex items-center justify-between py-2 px-3 rounded-lg border border-border bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <PhoneForwarded
                      className={`w-4 h-4 ${form.always_divert_to_operator ? 'text-amber-400' : 'text-muted-foreground'}`}
                    />
                    <div>
                      <Label className="text-foreground text-sm">Always Divert to Operator</Label>
                      <p className="text-xs text-muted-foreground">
                        The IVR transfers this caller to a human every time instead of booking automatically
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={form.always_divert_to_operator}
                    onCheckedChange={(checked) => updateField('always_divert_to_operator', checked)}
                  />
                </div>
              </div>

              {isEditing && (
                <div id="crm-form-addresses" className="border border-border rounded-lg p-4 bg-secondary/20 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        Bookable Addresses
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-2 px-3 rounded-lg border border-border bg-secondary/30">
                    <div className="flex items-center gap-2">
                      <ShieldCheck
                        className={`w-4 h-4 ${form.open_approval ? 'text-green-400' : 'text-muted-foreground'}`}
                      />
                      <div>
                        <Label className="text-foreground text-sm">Open Approval</Label>
                        <p className="text-xs text-muted-foreground">
                          Traveller is approved to travel to any destination
                        </p>
                      </div>
                    </div>
                    <Switch checked={form.open_approval} onCheckedChange={(checked) => updateField('open_approval', checked)} />
                  </div>

                  {form.open_approval && (
                    <div className="flex items-center gap-2 py-3 px-3 rounded-lg border border-green-700/40 bg-green-900/10">
                      <ShieldCheck className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <p className="text-sm text-green-400">
                        Open approval is enabled. This account can travel to any destination without specific address restrictions.
                      </p>
                    </div>
                  )}

                  <CRMBookableAddresses accountId={form.account_id} openApproval={form.open_approval} />
                </div>
              )}
            </TabsContent>

            <TabsContent value="hierarchy" className="mt-0 space-y-4">
              <div className="border border-border rounded-lg p-4 bg-secondary/20 space-y-4">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Account Relationships
                </p>

                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Hierarchy Type</Label>
                  <Select value={form.hierarchy_type} onValueChange={(v) => updateField('hierarchy_type', v)}>
                    <SelectTrigger className="dark-input">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">None</SelectItem>
                      {HIERARCHY_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Parent Account</Label>
                  {form.parent_account_id && !showParentSearch ? (
                    <div className="flex items-center gap-2 bg-secondary/50 border border-border rounded-md px-3 py-2">
                      <Link2 className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm text-foreground font-mono">{form.parent_account_id}</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="ml-auto text-xs text-muted-foreground h-6"
                        onClick={() => setShowParentSearch(true)}
                      >
                        Change
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-xs text-red-400 h-6"
                        onClick={() => {
                          updateField('parent_account_id', '');
                          setShowParentSearch(false);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={parentSearch}
                        onChange={(e) => {
                          setParentSearch(e.target.value);
                          setShowParentSearch(true);
                        }}
                        className="dark-input pl-9"
                        placeholder="Search by account ID or name..."
                      />
                      {searchingParent && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                      )}
                      {showParentSearch && parentResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-[200px] overflow-y-auto">
                          {parentResults.map((r) => (
                            <button
                              key={r.account_id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/50 transition-colors flex items-center gap-2"
                              onClick={() => {
                                updateField('parent_account_id', r.account_id);
                                setParentSearch('');
                                setShowParentSearch(false);
                              }}
                            >
                              <span className="font-mono text-xs text-muted-foreground">{r.account_id}</span>
                              <span className="text-foreground">{r.account_name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Link this account to a parent account for hierarchy grouping.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Billing Account (if different)</Label>
                  {form.billing_account_id && !showBillingSearch ? (
                    <div className="flex items-center gap-2 bg-secondary/50 border border-border rounded-md px-3 py-2">
                      <CreditCard className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm text-foreground font-mono">{form.billing_account_id}</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="ml-auto text-xs text-muted-foreground h-6"
                        onClick={() => setShowBillingSearch(true)}
                      >
                        Change
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-xs text-red-400 h-6"
                        onClick={() => {
                          updateField('billing_account_id', '');
                          setShowBillingSearch(false);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={billingSearch}
                        onChange={(e) => {
                          setBillingSearch(e.target.value);
                          setShowBillingSearch(true);
                        }}
                        className="dark-input pl-9"
                        placeholder="Search billing account..."
                      />
                      {searchingBilling && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                      )}
                      {showBillingSearch && billingResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-[200px] overflow-y-auto">
                          {billingResults.map((r) => (
                            <button
                              key={r.account_id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/50 transition-colors flex items-center gap-2"
                              onClick={() => {
                                updateField('billing_account_id', r.account_id);
                                setBillingSearch('');
                                setShowBillingSearch(false);
                              }}
                            >
                              <span className="font-mono text-xs text-muted-foreground">{r.account_id}</span>
                              <span className="text-foreground">{r.account_name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    If billing goes to a different account (e.g. a funder or parent), link it here.
                  </p>
                </div>
              </div>

              <div className="border border-border rounded-lg p-4 bg-secondary/20 space-y-3">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Inherited Defaults
                </p>
                {renderSwitch({
                  label: 'Use Parent Billing Defaults',
                  field: 'use_parent_billing_defaults',
                  description: 'Inherit billing details from parent account',
                })}
                {renderSwitch({
                  label: 'Use Parent Operational Defaults',
                  field: 'use_parent_operational_defaults',
                  description: 'Inherit Project/Cost Code/Reference from parent account',
                })}
              </div>

            </TabsContent>
            <TabsContent value="billing" className="mt-0 space-y-4">
              <div className="border border-border rounded-lg p-4 bg-secondary/20">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Billing Details
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField({ label: 'Billing Name', field: 'billing_name', placeholder: 'Billing entity name' })}
                  {renderField({ label: 'Billing Contact', field: 'billing_contact_name', placeholder: 'Billing contact person' })}
                  {renderField({ label: 'Billing Email', field: 'billing_email', placeholder: 'billing@example.com', type: 'email' })}
                  {renderField({ label: 'Billing Phone', field: 'billing_phone', placeholder: 'Billing phone', type: 'tel' })}
                  {renderField({ label: 'Address Line 1', field: 'billing_address_line_1', placeholder: 'Street address' })}
                  {renderField({ label: 'Address Line 2', field: 'billing_address_line_2', placeholder: 'Unit, suite, etc.' })}
                  {renderField({ label: 'Suburb', field: 'billing_suburb', placeholder: 'Suburb' })}
                  {renderField({ label: 'City', field: 'billing_city', placeholder: 'City' })}
                  {renderField({ label: 'Postcode', field: 'billing_postcode', placeholder: 'Postcode' })}
                  {renderField({ label: 'Attention', field: 'billing_attention', placeholder: 'Attention / FAO' })}
                  {renderField({ label: 'Invoice Email', field: 'invoice_email', placeholder: 'invoices@example.com', type: 'email' })}
                  {renderField({ label: 'Accounts Payable Email', field: 'accounts_payable_email', placeholder: 'ap@example.com', type: 'email' })}
                </div>
              </div>

              <div className="border border-border rounded-lg p-4 bg-secondary/20">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Payment Terms
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-foreground text-sm">Payment Terms</Label>
                    <Select value={form.payment_terms} onValueChange={(v) => updateField('payment_terms', v)}>
                      <SelectTrigger className="dark-input">
                        <SelectValue placeholder="Select terms..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Not Set</SelectItem>
                        {PAYMENT_TERMS_OPTIONS.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5" />
                  {renderTextarea({ label: 'Billing Notes', field: 'billing_notes', placeholder: 'Any billing-specific notes...', span2: true })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="traveller" className="mt-0 space-y-4">
              <div className="border border-border rounded-lg p-4 bg-secondary/20">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Traveller / Passenger Details
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderField({ label: 'First Name', field: 'traveller_first_name', placeholder: 'First name' })}
                  {renderField({ label: 'Last Name', field: 'traveller_last_name', placeholder: 'Last name' })}
                  {renderField({ label: 'Phone', field: 'traveller_phone', placeholder: 'Phone number', type: 'tel' })}
                  {renderField({ label: 'Mobile', field: 'traveller_mobile', placeholder: 'Mobile number', type: 'tel' })}
                  {renderField({ label: 'Email', field: 'traveller_email', placeholder: 'traveller@example.com', type: 'email', span2: true })}
                  {renderField({ label: 'External Client Ref', field: 'external_client_ref', placeholder: 'External reference ID', span2: true })}
                </div>
              </div>

              <div id="crm-form-transport" className="border border-border rounded-lg p-4 bg-secondary/20">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Transport Notes
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-foreground text-sm">Preferred Vehicle</Label>
                    <Select value={form.preferred_vehicle} onValueChange={(v) => updateField('preferred_vehicle', v)}>
                      <SelectTrigger className="dark-input">
                        <SelectValue placeholder="Select vehicle..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_specified">Not specified</SelectItem>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="suv_van">SUV/Van</SelectItem>
                        <SelectItem value="wheelchair_van">Wheelchair Van</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      What the IVR sends TaxiCaller for this customer's bookings, unless they ask for something else.
                    </p>
                  </div>
                  {renderTextarea({
                    label: 'Mobility Notes',
                    field: 'mobility_notes',
                    placeholder: 'Wheelchair, walker, assistance required...',
                    span2: true,
                  })}
                  {renderTextarea({
                    label: 'Pickup Notes',
                    field: 'pickup_notes',
                    placeholder: 'Default pickup instructions...',
                    span2: true,
                  })}
                  {renderTextarea({
                    label: 'Dropoff Notes',
                    field: 'dropoff_notes',
                    placeholder: 'Default dropoff instructions...',
                    span2: true,
                  })}
                  {renderTextarea({
                    label: 'Traveller Notes',
                    field: 'traveller_notes',
                    placeholder: 'General notes about this traveller...',
                    span2: true,
                  })}
                </div>
              </div>
            </TabsContent>


            <TabsContent value="operational" className="mt-0 space-y-4">
              <div className="border border-border rounded-lg p-4 bg-secondary/20 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <FileCheck className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      Approval Managed Summary
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Approval dates and operational defaults are no longer edited here. This form keeps the stored values for compatibility,
                      but day-to-day control should be managed from approvals rather than duplicated in two places.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Approval Status</span>
                    </div>
                    <Badge className={getStatusBadgeClass(approvalStatus.tone)}>{approvalStatus.label}</Badge>
                    <p className="text-xs text-muted-foreground">{approvalStatus.description}</p>
                  </div>

                  <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Approval Range</span>
                    </div>
                    <div className="text-sm text-foreground">
                      {form.active_from ? formatDate(displayToIso(form.active_from)) : 'Not set'} → {form.active_to ? formatDate(displayToIso(form.active_to)) : 'Not set'}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Read-only summary of the currently stored active dates.
                    </p>
                  </div>

                  <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Days Remaining</span>
                    </div>
                    <div className="text-sm text-foreground">
                      {approvalStatus.daysRemaining === null
                        ? 'N/A'
                        : approvalStatus.daysRemaining >= 0
                          ? `${approvalStatus.daysRemaining} day${approvalStatus.daysRemaining === 1 ? '' : 's'}`
                          : `${Math.abs(approvalStatus.daysRemaining)} day${Math.abs(approvalStatus.daysRemaining) === 1 ? '' : 's'} overdue`}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Calculated from the stored approval end date where available.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border border-border rounded-lg p-4 bg-secondary/20 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <Route className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      Stored Defaults Summary
                    </p>
                    <p className="text-sm text-muted-foreground">
                      These values are still being carried in the save payload for compatibility, but they are no longer manually edited here.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Badge className={defaultsConfiguredCount > 0 ? 'bg-blue-900/40 text-blue-400 border-blue-700/50' : 'bg-secondary text-muted-foreground border-border'}>
                    {defaultsConfiguredCount > 0 ? `${defaultsConfiguredCount} default${defaultsConfiguredCount === 1 ? '' : 's'} set` : 'No defaults set'}
                  </Badge>
                  {form.use_parent_operational_defaults && (
                    <Badge className="bg-amber-900/40 text-amber-400 border-amber-700/50">
                      Inheriting from parent
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {defaultSummaryItems.map((item) => (
                    <div key={item.label} className="rounded-lg border border-border bg-secondary/30 p-3">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{item.label}</p>
                      <p className={`text-sm ${item.value === 'Not set' ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-border rounded-lg p-4 bg-secondary/20 space-y-3">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Requirements
                </p>
                {renderSwitch({
                  label: 'Requires Project',
                  field: 'requires_project',
                  description: 'Jobs for this account must have a project field populated',
                })}
                {renderSwitch({
                  label: 'Requires Reference',
                  field: 'requires_reference',
                  description: 'Jobs must have a reference field populated',
                })}
                {renderSwitch({
                  label: 'Requires Cost Code',
                  field: 'requires_cost_code',
                  description: 'Jobs must have a cost code populated',
                })}
                {renderSwitch({
                  label: 'Requires Purchase Order',
                  field: 'requires_purchase_order',
                  description: 'Jobs for this account require a purchase order',
                })}
                {renderSwitch({
                  label: 'Allow Direct Settlement',
                  field: 'allow_direct_settlement',
                  description: 'Allow drivers to settle directly with this account',
                })}
              </div>

            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="flex-shrink-0 gap-2 border-t border-border pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border text-muted-foreground"
          >
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="golden-button">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {isEditing ? 'Update Account' : 'Create Account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CRMAccountForm;